import React, { useEffect, useRef, useState } from "react";
import Login from "../Login/Login";
import { formatPostDate, slugify } from "../../utils/slug";
import "../PostForm.css";
import "./AktualnosciManager.css";

const API_BASE = "https://ekselowe.pl/api";
const SITE_BASE = "https://ekselowe.pl";

// Formularz pól posta - używany zarówno do dodawania, jak i edycji wpisu.
// Obsługuje wiele zdjęć: jedno oznaczone jako miniaturka (widoczna na liście
// Aktualności), pozostałe pokazują się w treści wpisu.
const PostFieldsForm = ({ initial, submitLabel, isLoggedin, busy, onSubmit, onCancel }) => {
  const [title, setTitle] = useState(initial.title || "");
  const [text, setText] = useState(initial.text || "");
  const [keywords, setKeywords] = useState(initial.keywords || "");
  const [images, setImages] = useState(() => {
    const list = [];
    if (initial.image) list.push({ url: initial.image, thumbnail: true });
    if (Array.isArray(initial.images)) {
      initial.images.forEach((url) => {
        if (url) list.push({ url, thumbnail: false });
      });
    }
    return list;
  });
  const [urlInput, setUrlInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const fileInputRef = useRef(null);

  const addImage = (url, makeThumbnail) => {
    setImages((prev) => {
      const isFirst = prev.length === 0;
      const thumb = makeThumbnail || isFirst;
      const next = thumb ? prev.map((img) => ({ ...img, thumbnail: false })) : prev.slice();
      next.push({ url, thumbnail: thumb });
      return next;
    });
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const allowedExtension = [".jpg", ".jpeg", ".png"];
    const invalid = files.find(
      (f) => !allowedExtension.includes("." + f.name.split(".").pop().toLowerCase())
    );
    if (invalid) {
      setValidationError("Złe rozszerzenie. Należy użyć .jpg albo .png.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setValidationError(null);
    setUploading(true);
    try {
      for (const file of files) {
        const imageData = new FormData();
        imageData.append("file", file);
        imageData.append("token", isLoggedin);
        const response = await fetch(`${API_BASE}/imageupload.php`, {
          method: "POST",
          body: imageData,
        });
        const responseData = await response.json();
        if (responseData && responseData.image_link) {
          addImage("/public/uploads/" + responseData.image_link);
        }
      }
    } catch (err) {
      setValidationError("Nie udało się wrzucić pliku na serwer.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const addUrlImage = () => {
    if (!urlInput.trim()) return;
    addImage(urlInput.trim());
    setUrlInput("");
  };

  const setThumbnail = (index) => {
    setImages((prev) => prev.map((img, i) => ({ ...img, thumbnail: i === index })));
  };

  const removeImage = (index) => {
    setImages((prev) => {
      const removed = prev[index];
      const next = prev.filter((_, i) => i !== index);
      if (removed.thumbnail && next.length > 0) next[0] = { ...next[0], thumbnail: true };
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title || !text) return;
    const thumb = images.find((img) => img.thumbnail) || images[0];
    const rest = images.filter((img) => img !== thumb).map((img) => img.url);
    onSubmit({
      title,
      text,
      keywords,
      image: thumb ? thumb.url : "",
      file: "",
      images: JSON.stringify(rest),
    });
  };

  return (
    <form className="am-form" onSubmit={handleSubmit}>
      <div className="form-row">
        <input
          type="text"
          placeholder="Tytuł Posta"
          required
          className="form-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="form-row">
        <input
          type="text"
          placeholder="Słowa kluczowe (oddziel przecinkami, np. excel katowice, automatyzacja excel)"
          className="form-input"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
        />
      </div>
      <p className="am-image-add-hint">
        Słowa kluczowe pomagają Google zrozumieć, o czym jest wpis — wpisz frazy, po których
        klienci mogliby Cię szukać (np. „automatyzacja excel katowice”).
      </p>

      <div className="form-row am-image-add-row">
        <input
          placeholder="albo wklej URL obrazka"
          className="form-input"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
        />
        <div className="col col-3">
          <button className="btn" type="button" onClick={addUrlImage}>Dodaj URL</button>
        </div>
      </div>
      <div className="form-row am-image-add-row">
        <input
          type="file"
          ref={fileInputRef}
          accept=".jpg,.jpeg,.png"
          multiple
          onChange={handleFileChange}
        />
        {uploading && <span className="am-uploading-hint">Wysyłanie…</span>}
        {validationError && <span className="am-validation-error">{validationError}</span>}
      </div>
      <p className="am-image-add-hint">
        Możesz od razu zaznaczyć i wrzucić kilka zdjęć naraz (Ctrl / Cmd przy wyborze plików).
      </p>

      {images.length > 0 && (
        <div className="am-gallery">
          <p className="am-gallery-hint">
            Zaznacz, które zdjęcie ma być miniaturką (widoczne na liście Aktualności) — pozostałe
            pojawią się w treści wpisu.
          </p>
          <div className="am-gallery-grid">
            {images.map((img, i) => (
              <div
                className={"am-gallery-item" + (img.thumbnail ? " is-thumbnail" : "")}
                key={img.url + i}
              >
                <img src={img.url} alt="" className="am-gallery-thumb" />
                <label className="am-gallery-radio">
                  <input
                    type="radio"
                    name="thumbnail-pick"
                    checked={img.thumbnail}
                    onChange={() => setThumbnail(i)}
                  />
                  Miniaturka
                </label>
                <button type="button" className="am-gallery-remove" onClick={() => removeImage(i)}>
                  Usuń
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <textarea
        placeholder="Treść posta"
        required
        className="form-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="am-form-actions">
        <button type="submit" className="submit-button" disabled={busy}>
          {busy ? "Zapisywanie…" : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel}>Anuluj</button>
        )}
      </div>
    </form>
  );
};

const AktualnosciManager = ({ isLoggedin, setIsLoggedin }) => {
  const [posts, setPosts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchPosts = () => {
    setLoading(true);
    fetch(`${API_BASE}/getposts.php`, {
      headers: { "Content-Type": "application/json; charset=UTF-8" },
    })
      .then((res) => res.json())
      .then((json) => setPosts(json || {}))
      .catch(() => setPosts({}))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isLoggedin) fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedin]);

  if (!isLoggedin) {
    return <Login setToken={setIsLoggedin} />;
  }

  const submitToApi = async (url, fields) => {
    setBusy(true);
    try {
      const body = new URLSearchParams({
        title: fields.title,
        text: fields.text,
        keywords: fields.keywords || "",
        image: fields.image || "",
        file: fields.file || "",
        images: fields.images || "[]",
        token: isLoggedin,
        ...(fields.id !== undefined ? { id: fields.id } : {}),
      });
      await fetch(url, { method: "POST", body });
      fetchPosts();
      setShowAddForm(false);
      setEditingKey(null);
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = (fields) => submitToApi(`${API_BASE}/addpost.php`, fields);
  const handleEditSubmit = (key, fields) => submitToApi(`${API_BASE}/editpost.php`, { ...fields, id: key });

  const handleDelete = async (key, title) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć post "${title}"?`)) return;
    setBusy(true);
    try {
      await fetch(`${API_BASE}/deletepost.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idd: key, token: isLoggedin }),
      });
      fetchPosts();
    } finally {
      setBusy(false);
    }
  };

  const keys = Object.keys(posts).reverse();

  return (
    <div className="am-wrapper">
      <h1 className="am-title">Zarządzaj Aktualnościami</h1>
      <p className="am-subtitle">Tutaj dodajesz, edytujesz i usuwasz wpisy widoczne na stronie „Aktualności”.</p>

      {!showAddForm && (
        <button className="submit-button am-add-toggle" onClick={() => { setShowAddForm(true); setEditingKey(null); }}>
          + Dodaj nowy wpis
        </button>
      )}

      {showAddForm && (
        <div className="am-card am-add-card">
          <h2>Nowy wpis</h2>
          <PostFieldsForm
            initial={{ title: "", text: "", image: "", images: [] }}
            submitLabel="Dodaj wpis"
            isLoggedin={isLoggedin}
            busy={busy}
            onSubmit={handleAdd}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}

      {loading && <p>Wczytywanie wpisów…</p>}

      <div className="am-list">
        {!loading && keys.length === 0 && <p>Nie masz jeszcze żadnych wpisów.</p>}
        {keys.map((key) => {
          const post = posts[key];
          const isEditing = editingKey === key;
          return (
            <div className="am-card" key={key}>
              {isEditing ? (
                <>
                  <h2>Edytuj wpis</h2>
                  <PostFieldsForm
                    initial={post}
                    submitLabel="Zapisz zmiany"
                    isLoggedin={isLoggedin}
                    busy={busy}
                    onSubmit={(fields) => handleEditSubmit(key, fields)}
                    onCancel={() => setEditingKey(null)}
                  />
                </>
              ) : (
                <div className="am-row">
                  {post.image && <img src={post.image} alt={post.title} className="am-thumb" />}
                  <div className="am-row-text">
                    <h3>{post.title}</h3>
                    {post.date && <p className="am-row-date">{formatPostDate(post.date)}</p>}
                    <p>{post.text}</p>
                  </div>
                  <div className="am-row-actions">
                    <a
                      className="btn am-fb-share"
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                        `${SITE_BASE}/aktualnosci/${slugify(post.title)}`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Udostępnij na FB
                    </a>
                    <button className="btn" onClick={() => { setEditingKey(key); setShowAddForm(false); }}>Edytuj</button>
                    <button className="btn am-delete" onClick={() => handleDelete(key, post.title)}>Usuń</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AktualnosciManager;
